// RoughNightToggle — Phase D.1 §P10.9. A one-tap surface that flips
// the 12-hour "soften everything" mode. Lives in the Today screen's
// Tools accordion so it stays out of the primary flow but is one tap
// away on a tough night.
//
// Voice: never patronizing. Inactive copy frames it as a tool the user
// already knows they need ("Bad night?"); active copy is matter-of-fact
// confirmation with an "End early" escape hatch.

import { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useRoughNight } from '../../hooks/useRoughNight';
import { useToast } from '../ui/Toast';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';

export function RoughNightToggle() {
  const { active, until, activate, deactivate } = useRoughNight();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      if (active) {
        await deactivate();
        toast.push('Soften mode off');
      } else {
        await activate();
        toast.push('Soften mode on for 12 hours');
      }
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not update — try again.'), { tone: 'error' });
      }
    } finally {
      setBusy(false);
    }
  }

  if (active && until) {
    const endTime = until.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return (
      <Card
        variant="inset"
        className="rough-night-active"
        role="region"
        aria-label="Soften mode active"
      >
        <p className="text-caption" style={{ marginBlockEnd: 'var(--space-2)' }}>
          Soften mode on. Active until {endTime}.
        </p>
        <Button
          variant="tertiary"
          size="md"
          onClick={() => void toggle()}
          loading={busy}
        >
          End early
        </Button>
      </Card>
    );
  }

  return (
    <Card variant="inset" className="rough-night-inactive">
      <p className="text-caption muted" style={{ marginBlockEnd: 'var(--space-2)' }}>
        Bad night? You can soften the app for 12 hours — no streak,
        no charts, just support.
      </p>
      <Button
        variant="tertiary"
        size="md"
        onClick={() => void toggle()}
        loading={busy}
      >
        Tonight is rough — soften for 12h
      </Button>
    </Card>
  );
}
